import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import axios from "axios";
import "../../../../ComponentsCss/GenerateSalaryComponent/PayRole/PayAdjustment/PayAdjustment.css";
import { usePopup } from "../../../../../context/popup-context/Popup";
import { Popup } from "../../../../Utils/Popup/Popup";
import { GlobalContext } from "../../../../../context/GlobalContext/GlobalContext";

export default function PayAdjustment() {
  const [searchTerm, setSearchTerm] = useState("");
  const [employees, setEmployees] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAdjustmentPopup, setShowAdjustmentPopup] = useState(false);

  const [showImportPopup, setShowImportPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const [variableComponents, setVariableComponents] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [adjustmentRows, setAdjustmentRows] = useState([]);
  
  const { showPopup, setShowPopup, setMessage } = usePopup();
  
  // ✅ GRAB CONTEXT DATA
  const { globalData } = useContext(GlobalContext);
  const companyCode = globalData?.userInfo?.companyCode;
  const month = globalData?.month;
  const year = globalData?.year;
  const { startDate, endDate, periodKey } = globalData?.payCycle || {};

  const wrapperRef = useRef();

  const getCookieValue = (name) =>
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`))
      ?.split("=")[1];

  const token = useMemo(() => getCookieValue("authToken"), []);

  const downloadBlob = (data, fileName) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchVariableComponents = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/pay_adjustment/get_variable_components`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setVariableComponents(res.data.data);
      }
    } catch (error) {
      console.log("Error fetching components", error);
    }
  }, [token]);

  // ✅ FETCH EXISTING ADJUSTMENTS BY PERIOD KEY
  const fetchExistingAdjustments = useCallback(async () => {
    if (!companyCode || !periodKey) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/pay_adjustment/get_adjustments`,
        {
          params: { companyCode, periodKey },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.success) {
        const loadedRows = [];
        res.data.data.forEach((doc) => {
          doc.adjustments.forEach((adj) => {
            loadedRows.push({
              code: doc.employee_id,
              name: doc.employee_name,
              componentId: adj.component_id,
              componentName: adj.component_name,
              amount: adj.amount,
              type: adj.type,
            });
          });
        });
        setAdjustmentRows(loadedRows);
      }
    } catch (error) {
      console.log("Error fetching existing adjustments", error);
    }
  }, [companyCode, periodKey, token]);

  useEffect(() => {
    fetchVariableComponents();
  }, [fetchVariableComponents]);

  // Reactively fetch data when the Month/Year/Period changes
  useEffect(() => {
    fetchExistingAdjustments();
  }, [fetchExistingAdjustments]);

  const handleSaveManualAdjustment = () => {
    if (!selectedEmployee) return;

    const draftRows = variableComponents
      .map((comp) => ({
        code: selectedEmployee.employee_id,
        name: selectedEmployee.employee_details?.name,
        componentId: comp._id,
        componentName: comp.name,
        amount: parseFloat(inputValues[comp._id]) || 0,
        type: comp.pay_type,
      }))
      .filter((row) => row.amount !== 0);

    if (!draftRows.length) {
      setShowPopup(true);
      setMessage("Please enter at least one amount");
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    setAdjustmentRows((prev) => [
      ...prev.filter((r) => r.code !== selectedEmployee.employee_id),
      ...draftRows,
    ]);

    setShowAdjustmentPopup(false);
    setInputValues({});
    setSelectedEmployee(null);
    setSearchTerm("");
  };

  // ✅ FINAL SAVE - POSTS DATES AND PERIOD KEY
  const handleFinalSave = async () => {
    if (!adjustmentRows.length) {
      setShowPopup(true);
      setMessage("No adjustments to save");
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    try {
      const groupedByEmployee = {};

      adjustmentRows.forEach((row) => {
        if (!groupedByEmployee[row.code]) {
          groupedByEmployee[row.code] = { employeeName: row.name, adjustments: [] };
        }
        groupedByEmployee[row.code].adjustments.push({
          component_id: row.componentId,
          component_name: row.componentName,
          amount: row.amount,
          type: row.type,
        });
      });

      for (const employeeId of Object.keys(groupedByEmployee)) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/pay_adjustment/save_employee_adjustment`,
          {
            companyCode,
            employeeId,
            employeeName: groupedByEmployee[employeeId].employeeName,
            periodKey,
            startDate,
            endDate,
            adjustments: groupedByEmployee[employeeId].adjustments,
            uploadedBy: "HR Final Save",
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      setShowPopup(true);
      setMessage("All adjustments saved successfully ✅");
      setTimeout(() => setShowPopup(false), 3000);
    } catch (err) {
      console.error(err);
      setShowPopup(true);
      setMessage("Final save failed");
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const fetchEmployees = useCallback(
    async (value) => {
      if (!companyCode || !value.trim()) {
        setEmployees([]);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/company/${companyCode}/employees`,
          {
            params: { search: value },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setEmployees(res.data.employees || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error("Employee Search Error:", err);
      }
    },
    [companyCode, token],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchEmployees(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchEmployees]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
    setShowSuggestions(false);
    setShowAdjustmentPopup(true);
    setSearchTerm("");
  };

  const handleActionChange = (e) => {
    if (e.target.value === "import_pay_adjustment") {
      setShowImportPopup(true);
      e.target.value = "";
    }
  };

  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  // ✅ UPLOAD FILE - APPENDS PERIOD DATA
  const handleUploadFile = async () => {
    if (!selectedFile) {
      setShowPopup(true);
      setMessage("Please select file");
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("companyCode", companyCode);
    formData.append("periodKey", periodKey);
    formData.append("startDate", startDate);
    formData.append("endDate", endDate);

    try {
      setIsUploading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/pay_adjustment/upload_payAdjustment`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const { errorReport, stats, message } = res.data;

      if (errorReport) {
        setShowPopup(true);
        setMessage(`Upload completed with errors (${stats.failedRows})`);
        setTimeout(() => setShowPopup(false), 3000);
        const byteArray = Uint8Array.from(atob(errorReport), (c) => c.charCodeAt(0));
        downloadBlob(byteArray, "Upload_Errors.xlsx");
      } else {
        setShowPopup(true);
        setMessage(message || "Upload Success");
        setTimeout(() => setShowPopup(false), 3000);
        fetchExistingAdjustments(); // Refresh UI
      }

      setShowImportPopup(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload Error:", err);
      setShowPopup(true);
      setMessage(err.response?.data?.message || "Upload Failed");
      setTimeout(() => setShowPopup(false), 3000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteRow = (employeeId) => {
    setAdjustmentRows((prev) => prev.filter((r) => r.code !== employeeId));
  };

  // ✅ DOWNLOAD TEMPLATE - PASSES PERIOD KEY
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/pay_adjustment/download_salaryAdjustment`,
        {
          params: { companyCode, month, year, periodKey },
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      downloadBlob(res.data, `Salary_Adjustment_${month}_${year}.xlsx`);
    } catch (err) {
      console.error("Template Download Error:", err);
      setShowPopup(true);
      setMessage("Download Failed");
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  return (
    <div className="pay-adjustment-container">
      <div className="controls-pay-adjustment">
        <div className="search-wrapper" ref={wrapperRef}>
          <div className="controls-section">
            <input
              type="text"
              placeholder="Search Employee - Code / Name"
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => employees.length && setShowSuggestions(true)}
            />
            {searchTerm && (
              <span
                className="clear-search"
                onClick={() => {
                  setSearchTerm("");
                  setEmployees([]);
                  setShowSuggestions(false);
                }}
              >
                ✕
              </span>
            )}
          </div>
          {showSuggestions && (
            <div className="suggestion-box">
              {employees.length === 0 ? (
                <div className="no-suggestion">No Employees Found</div>
              ) : (
                employees.map((emp) => (
                  <div key={emp._id} className="suggestion-item" onClick={() => handleEmployeeSelect(emp)}>
                    <h5>{emp.employee_id}</h5>
                    <span>{emp.employee_details?.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <select className="filter-select" onChange={handleActionChange} defaultValue="">
          <option value="" disabled>Select Action</option>
          <option value="employee_wise">Employee Wise</option>
          <option value="component_wise">Component Wise</option>
          <option value="import_pay_adjustment">Import Pay Adjustment</option>
        </select>
      </div>

      <div className="table-container__pay_adjustment">
        <table className="attendance-table__pay_adjustment">
          <thead>
            <tr>
              <th>SL.NO</th>
              <th>CODE</th>
              <th>NAME</th>
              <th>SALARY COMPONENT</th>
              <th>AMOUNT</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {adjustmentRows.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data-cell">No Adjustments Added</td>
              </tr>
            ) : (
              adjustmentRows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.componentName}</td>
                  <td>{row.amount}</td>
                  <td>
                    <button onClick={() => handleDeleteRow(row.code)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="final-save-container">
          <button className="primary-btn_adjustment final-save-btn" onClick={handleFinalSave}>
            Save
          </button>
        </div>
      </div>

      {showAdjustmentPopup && selectedEmployee && (
        <div className="popup-overlay">
          <div className="popup-content large-popup" style={{ width: "650px" }}>
            <div className="popup-header">
              <h3>
                Add Employee Adjustment
                <br />
                <span style={{ fontSize: "13px", color: "#666", fontWeight: "normal" }}>
                  {selectedEmployee.employee_details?.name} ({selectedEmployee.employee_id})
                </span>
              </h3>
              <button className="close-popup-btn" onClick={() => setShowAdjustmentPopup(false)}>&times;</button>
            </div>

            <div className="popup-body-scrollable">
              <div style={{ marginBottom: "15px", padding: "10px", background: "#f8f9fa", borderRadius: "5px", fontSize: "13px" }}>
                <strong>Note:</strong> Enter amounts for the components you want to adjust. Leave blank or 0 to ignore.
              </div>
              <table className="attendance-table__pay_adjustment" style={{ marginTop: "0" }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}>Component Name</th>
                    <th style={{ width: "30%" }}>Pay Type</th>
                    <th style={{ width: "30%" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {variableComponents.length > 0 ? (
                    variableComponents.map((comp) => (
                      <tr key={comp._id}>
                        <td style={{ textAlign: "left", fontWeight: "500" }}>{comp.name}</td>
                        <td>
                          <span style={{
                            padding: "2px 8px", borderRadius: "10px", fontSize: "11px",
                            backgroundColor: comp.pay_type === "Deduction" ? "#ffebee" : "#e8f5e9",
                            color: comp.pay_type === "Deduction" ? "#c62828" : "#2e7d32",
                            border: `1px solid ${comp.pay_type === "Deduction" ? "#ffcdd2" : "#c8e6c9"}`
                          }}>
                            {comp.pay_type}
                          </span>
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="0"
                            className="search-input"
                            style={{ width: "100%", height: "30px", textAlign: "right" }}
                            value={inputValues[comp._id] || ""}
                            onChange={(e) => setInputValues((prev) => ({ ...prev, [comp._id]: e.target.value }))}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="no-data-cell">No variable components found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="popup-footer">
              <button className="primary-btn_adjustment" onClick={handleSaveManualAdjustment}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportPopup && (
        <div className="popup-overlay">
          <div className="popup-content large-popup">
            <div className="popup-header">
              <h3>Import Pay Adjustment</h3>
              <button onClick={() => setShowImportPopup(false)}>&times;</button>
            </div>
            <div className="popup-body-scrollable">
              <div className="instruction-section">
                <h4>Instructions for using this import template</h4>
                <p>You can use this template to import bulk input in variable pay components.</p>
                <div className="note-section">
                  <h5>⚠️ Please Note:</h5>
                  <ul>
                    <li>If you do not see a particular earning/deduction component...</li>
                    <li>Don’t change the names of any sheets...</li>
                    <li>You should not add any extra sheets...</li>
                    <li>This file cannot contain formulas...</li>
                  </ul>
                </div>
                <div className="upload-section">
                  <label>Upload Filled File:</label>
                  <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                </div>
              </div>
              <div className="popup-footer">
                <button onClick={handleDownloadTemplate}>⬇ Download Template</button>
                <button onClick={handleUploadFile} disabled={isUploading} className="primary-btn_adjustment">
                  {isUploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPopup && <Popup />}
    </div>
  );
}